import { useEffect, useState } from 'react';
import {
  initializeDiscord,
  isRunningInDiscord,
  hasDiscordFeatures,
  getUserDisplayName,
  generateSessionId,
  updateDiscordActivity,
  getDiscordUser,
  getDiscordGuild,
} from '../utils/discordUtils';

export interface DiscordIntegration {
  isDiscordEnvironment: boolean;
  isDiscordReady: boolean;
  discordUser: any;
  discordGuild: any;
  sessionId: string;
  userDisplayName: string;
  updateActivity: (activity: any) => Promise<void>;
}

export function useDiscordIntegration(): DiscordIntegration {
  const [isDiscordEnvironment, setIsDiscordEnvironment] = useState(false);
  const [isDiscordReady, setIsDiscordReady] = useState(false);
  const [discordUser, setDiscordUser] = useState(null);
  const [discordGuild, setDiscordGuild] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');

  useEffect(() => {
    const initDiscord = async () => {
      const isDiscord = isRunningInDiscord();
      setIsDiscordEnvironment(isDiscord);

      if (isDiscord) {
        console.log('🎮 Initializing Discord integration...');
        const success = await initializeDiscord();
        
        if (success) {
          setIsDiscordReady(true);
          setDiscordUser(getDiscordUser());
          setDiscordGuild(getDiscordGuild());
          console.log('✅ Discord integration ready!');
        } else {
          console.warn('⚠️ Discord integration failed, falling back to web mode');
        }
      }

      // Generate session ID and user display name (works for both Discord and web)
      setSessionId(generateSessionId());
      setUserDisplayName(getUserDisplayName());
    };

    initDiscord();
  }, []);

  const updateActivity = async (activity: any) => {
    if (hasDiscordFeatures()) {
      try {
        await updateDiscordActivity(activity);
      } catch (error) {
        // Properly serialize the error for Discord's logging system
        const errorDetails = {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          activity: JSON.stringify(activity),
          sessionId: sessionId,
          userDisplayName: userDisplayName,
          isDiscordReady: isDiscordReady
        };
        
        console.error('Discord activity update failed in hook:', errorDetails);
        console.error('Raw error object:', error);
      }
    }
  };

  return {
    isDiscordEnvironment,
    isDiscordReady,
    discordUser,
    discordGuild,
    sessionId,
    userDisplayName,
    updateActivity,
  };
}